import i18n from "i18next";
import {
  getGridDateOperators,
  getGridNumericOperators,
  getGridStringOperators,
  getGridSingleSelectOperators,
  GridToolbarExport,
} from "@mui/x-data-grid-pro";
import moment from "moment";

import httpClient from "../../lib/httpClient";
import currentClientConfig from "../../config/index";

const DATETIME_FORMAT = process.env.REACT_APP_DATETIME_FORMAT;

export function handleNullValue(rowDataValue) {
  return rowDataValue?.toString().trim() ? rowDataValue : "-";
}

// TODO: rename this function
export const tableCellRender = (rowDataValue) => {
  if (typeof rowDataValue === "number") return i18n.t("numberFormat", { value: rowDataValue });
  return handleNullValue(rowDataValue);
};

export const fromStringToNumberParser = (value) => {
  const numberValue = Number(value);
  return isNaN(numberValue) ? null : numberValue;
};

export const oneDecimalFormat = (rowDataValue) => {
  const currentLanguage = i18n.language;

  return new Intl.NumberFormat(currentLanguage, {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  }).format(rowDataValue);
};

export const twoDecimalsFormat = (rowDataValue) => {
  const currentLanguage = i18n.language;

  return new Intl.NumberFormat(currentLanguage, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(rowDataValue);
};

export const fourDecimalsFormat = (rowDataValue) => {
  const currentLanguage = i18n.language;

  return new Intl.NumberFormat(currentLanguage, {
    minimumFractionDigits: 4,
    maximumFractionDigits: 4,
  }).format(rowDataValue);
};

export const formatDateValue = (dateValue) => `${new Date(dateValue)?.toLocaleDateString(DATETIME_FORMAT)}`;

export const formatDateTimeValue = (dateValue) => `${new Date(dateValue)?.toLocaleString(DATETIME_FORMAT)}`;

export const dateFilterOperators = getGridDateOperators().filter((operator) =>
  ["is", "after", "before"].includes(operator.value),
);

export const numberFilterOperators = getGridNumericOperators().filter((operator) =>
  ["=", ">", "<"].includes(operator.value),
);

export const stringFilterOperators = getGridStringOperators()
  .filter((operator) => ["contains", "equals"].includes(operator.value))
  .map((operator) => {
    if (operator.value === "equals") {
      return {
        ...operator,
        label: "equals (a≠A)", // Change the display text
      };
    }
    return operator;
  })
  .reverse();

export const isSingleSelectOperators = getGridSingleSelectOperators().filter(
  (operator) => operator.value === "is",
);

export const getRandomInt = () => Math.floor(Math.random() * 100000);

const getLatestUpdate = async (serviceLogUrl) => {
  try {
    const { data: lastUpdate } = await httpClient().get(serviceLogUrl);

    const lastUpdateDate = moment(lastUpdate).format("DD/MM/YYYY");
    const lastUpdateDateMsg = moment(lastUpdateDate, "DD/MM/YYYY").isValid()
      ? `${i18n.t("policy.databaseLastUpdate")} ${lastUpdateDate}`
      : "";
    // If resource path is incorrect, "alive" is received.
    // if servervicelog service is missing, "" is received.
    // In both cases they are not valid dates.
    return lastUpdateDateMsg;
  } catch (err) {
    console.error(err);
    return ""; // Lost contact to backend
  }
};

export const getLatestUpdateDaily = async (serviceLogUrl) => {
  const lastUpdateDateMsg = (await getLatestUpdate(serviceLogUrl)) + " . " + i18n.t("policy.updatedDaily");
  return lastUpdateDateMsg;
};

export const getLatestUpdateAgro = async (serviceLogUrl) => {
  // Same as getLatestUpdateDaily, but with different caller
  const lastUpdateDateMsg = (await getLatestUpdate(serviceLogUrl)) + " . " + i18n.t("policy.updatedDaily");
  return lastUpdateDateMsg;
};

export const getLatestUpdateMonth = async (serviceLogUrl) => {
  try {
    const { data: lastUpdate } = await httpClient().get(serviceLogUrl);
    const lastUpdateDate = moment(lastUpdate).format("MM/YYYY");
    const lastUpdateDateMsg = moment(lastUpdateDate, "MM/YYYY").isValid()
      ? `${i18n.t("policy.databaseLastUpdateMonth")} ${lastUpdateDate} . ${i18n.t(
          "policy.updatedMonthly11th",
        )}`
      : "";
    // If resource path is incorrect, "alive" is received.
    // if servervicelog service is missing, "" is received.
    // In both cases they are not valid dates.
    return lastUpdateDateMsg;
  } catch (err) {
    console.error("Catch in getLatestUpdateMonth:", err);
    return ""; // Lost contact to backend
  }
};

export const getCompanyCodeName = (companyCodeValue, clientConfigCompanyCodes) => {
  const currCompanyCode = clientConfigCompanyCodes.find(
    (companyCode) => companyCode.value === companyCodeValue,
  );

  return currCompanyCode ? currCompanyCode.name : companyCodeValue;
};

export const getCompanyCodeFilter = (companyCodeMap) => {
  const companyCodeFilter = companyCodeMap.map((companyCode) => ({
    label: companyCode.name,
    value: companyCode.value,
  }));

  return companyCodeFilter;
};

const validRegex = (input) => {
  // Note, this can handle such cases as unclosed parentheses, but not corner cases like '*' and '[]'.
  // This is because Javascript Regex is not exectly the same as PERL regex which is implemented in Mongo DB
  // requests, according to their documentation.
  // In general, passing regex from user to mongo requests directly is not a good idea, because it is not secure, but we are doing it.
  try {
    new RegExp(input);
    return true;
  } catch (e) {
    return false;
  }
};

const isEmptyObject = (obj) => {
  return obj != null && obj !== undefined && Object.keys(obj).length === 0 && obj.constructor === Object;
};

export const filterModelToQueryValue = (filterModel) => {
  if (isEmptyObject(filterModel)) {
    return "[]";
  }

  const filterItems = filterModel.items;

  let filters = filterItems.map((filterItem) => {
    if (filterItem.value) {
      if (validRegex(filterItem.value)) {
        return {
          [filterItem.field]: {
            value: filterItem.value,
            operator: filterItem.operator,
          },
        };
      } else return null;
    } else return null;
  });
  if (filters) {
    filters = filters.filter((a) => a);
    // remove nulls, to ignore invalid regexes such as '(' silently
  }

  return JSON.stringify(filters);
};

export const addExportButton = (fileNamePrefix) => {
  const now = new Date();
  const date = now.toISOString().slice(0, 10);
  const time = now.toISOString().slice(11, 19).replace(/:/g, "-");
  const fileNameForCsv = "Cys_" + fileNamePrefix + "_" + date + "_" + time;
  return <GridToolbarExport csvOptions={{ delimiter: ";", utf8WithBom: true, fileName: fileNameForCsv }} />;
};

export const buildingCompanyColumnFeatures = {
  width: 70,
  type: "singleSelect",
  filterOperators: isSingleSelectOperators,
  valueOptions: getCompanyCodeFilter(currentClientConfig.companyCodesMap),
  valueGetter: (params) => getCompanyCodeName(params.row.companyCode, currentClientConfig.companyCodesMap),
};

export const round1 = (arg) => Math.round(arg * 10) / 10;
export const round2 = (arg) => Math.round(arg * 100) / 100;
export const round3 = (arg) => Math.round(arg * 1000) / 1000;
const round4 = (arg) => Math.round(arg * 10000) / 10000;

export const fixedFormatter = {
  type: "number",
  valueFormatter: (params) => tableCellRender(Math.round(params.value)),
};

export const twoDecimalsFormatter = {
  type: "number",
  valueFormatter: (params) => twoDecimalsFormat(round2(params.value)),
};

export const oneDecimalFormatter = {
  type: "number",
  valueFormatter: (params) => oneDecimalFormat(round1(params.value)),
};

export const percent100Formatter = {
  type: "number",
  valueFormatter: (params) => tableCellRender((params.value * 100) | 0) + "%",
};

export const percent100twoDecimalsFormatter = {
  type: "number",
  valueFormatter: (params) => twoDecimalsFormat(round2(params.value * 100)) + "%",
};

export const fourDecimalsFormatter = {
  type: "number",
  valueFormatter: (params) => fourDecimalsFormat(round4(params.value)),
};

export const capitalizeFirstLetter = (string) => {
  if (!string) return "NULL";
  if (typeof string != "string") return string;
  if (string.length <= 1) return string;
  return string.charAt(0).toUpperCase() + string.slice(1).toLowerCase();
};

export const capitalizeFirstLetterFormatter = {
  valueFormatter: ({ value }) => capitalizeFirstLetter(value),
};

export const null2DashFormatter = {
  valueFormatter: ({ value }) => (value ? value : "-"),
};

export const formatCamelCaseToSeparateWords = (str) => {
  return str
    .replace(/([a-z])([A-Z])/g, "$1 $2") // insert space before uppercase letters
    .replace(/^./, (match) => match.toUpperCase()); // capitalize the first letter
};
